import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { useDashboardMode } from '../../contexts/DashboardModeContext';
import { useAuthStore } from '../../stores/authStore';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { isAllInOneMode } = useDashboardMode();
  const {
    accessContext,
    accessContextStatus,
    accessContextError,
    loadAccessContext,
  } = useAuthStore();
  const location = useLocation();
  
  // Check if we're on the dashboard page
  const isDashboardPage = location.pathname === '/dashboard' || location.pathname === '/';
  
  // Initialize sidebar state from localStorage, default to true for desktop
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    console.log('Loading sidebar state from localStorage:', saved);
    if (saved !== null) {
      const parsed = JSON.parse(saved);
      return parsed;
    }
    // Default to true on desktop, false on mobile
    const defaultState = window.innerWidth >= 1024;
    return defaultState;
  });

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  // No automatic resize behavior - sidebar state should only change via manual toggle
  // The user wants the sidebar to persist exactly as they set it

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // In all-in-one mode, use full screen without navbar/sidebar, but only on dashboard page
  if (isAllInOneMode && isDashboardPage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="min-h-screen">
          <div className="min-h-screen">
            {children}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <Navbar 
        onToggleSidebar={toggleSidebar} 
        isSidebarOpen={isSidebarOpen}
      />

      {/* Sidebar - Always rendered, positioned fixed */}
      <Sidebar 
        isOpen={isSidebarOpen} 
        onClose={closeSidebar}
      />

      {/* Main Content with dynamic left margin for desktop sidebar */}
      <main className={`pt-16 min-h-screen transition-all duration-300 ease-in-out ${
        isSidebarOpen ? 'lg:ml-64' : 'lg:ml-0'
      }`}>
        <div className="min-h-[calc(100vh-4rem)]">
          {accessContextStatus === 'error' ? (
            <div
              className="mx-4 mt-4 flex items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 lg:mx-6"
              role="alert"
            >
              <span>
                Plant access could not be loaded. Data is hidden until access can be verified.
                {accessContextError ? ` ${accessContextError}` : ''}
              </span>
              <button
                type="button"
                onClick={() => void loadAccessContext(true)}
                className="shrink-0 rounded-md border border-red-300 bg-white px-3 py-1.5 font-medium text-red-800 hover:bg-red-100"
              >
                Retry
              </button>
            </div>
          ) : null}
          {accessContext?.warnings?.length ? (
            <div
              className="mx-4 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 lg:mx-6"
              role="status"
            >
              {accessContext.warnings.map((warning) => (
                <div key={warning.code}>{warning.message}</div>
              ))}
            </div>
          ) : null}
          {children}
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
